import { RouterState } from "@/interfaces/Router";
import { auth, provider } from "@/lib/firebase";
import {
  isEmailValid,
  isPasswordValid,
  onLoginSuccess,
  translateError,
  isUserFromRussia,
} from "@/lib/utils";
import UserService from "@/services/user";
import {
  FINISH_GOOGLE_AUTH_ROUTE,
  RECOVER_PASSWORD_ROUTE,
  REGISTER_ROUTE,
  USER_TOKEN,
} from "@/shared/constants";
import {
  Alert,
  Box,
  Button,
  Divider,
  Stack,
  TextField,
  Typography,
  Link,
  Tooltip,
} from "@mui/material";
import { Google as GoogleIcon } from "@mui/icons-material";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getAdditionalUserInfo,
  signInWithEmailAndPassword,
  signInWithPopup,
} from "firebase/auth";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import AuthWrapper from "./AuthWrapper";
import LoadingOverlay from "./LoadingOverlay";
import PasswordField from "./PasswordField";

// Decrease popup cancel delay. Sketchy? yes
(function () {
  const originalSetTimeout: typeof setTimeout = window.setTimeout;

  window.setTimeout = function (
    handler: TimerHandler,
    timeout?: number | undefined,
    ...args: any[]
  ): number {
    // Check if the delay matches Firebase's *Timeout.AUTH*EVENT
    if (timeout === 8000) {
      timeout = 1000;
    }
    return originalSetTimeout(handler, timeout, ...args);
  } as typeof setTimeout;

  // Preserve the __promisify__ property
  (window.setTimeout as any).__promisify__ = originalSetTimeout.__promisify__;
})();

// validate inputs on submit and onChange
export default function Login() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { state } = useLocation() as { state: RouterState };
  const emailFromRouter = state?.email || "";
  const isProduction = process.env.NODE_ENV === "production";

  // $ Получаем токен приглашения из URL параметров
  const [invitationToken, setInvitationToken] = useState<string | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("invitationToken");
    console.debug(
      "$ Login: Component mounted, URL search:",
      window.location.search,
    );
    console.debug("$ Login: Token from URL:", token);
    if (token) {
      console.debug("$ Login: Получен токен приглашения из URL", token);
      setInvitationToken(token);
    }
  }, []);

  const { data: isRussianUser = false, isPending: isRegionPending } = useQuery({
    queryKey: ["isRussianUser"],
    queryFn: isUserFromRussia,
    staleTime: Infinity,
  });

  const [wasValidated, setWasValidated] = useState(false);
  const [email, setEmail] = useState(emailFromRouter);
  const [password, setPassword] = useState("");
  // error is ref, not state, because it's value shouldn't be changing together with inputs. And setWasValidated will trigger re-render anyway when error should be shown
  const errorRef = useRef<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const onError = (error: unknown) => {
    errorRef.current = translateError(error);
    console.error(error);

    // reset errors, since server return an error, any field could be invalid
    setWasValidated(false);
  };

  const signInWithEmailMutation = useMutation({
    mutationFn: async ({
      email,
      password,
    }: {
      email: string;
      password: string;
    }) => {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await user.getIdToken();

      await UserService.login({ idToken });

      return user;
    },
    onSuccess: () => {
      // $ Передаем токен приглашения в функцию onLoginSuccess
      console.debug(
        "$ Login: Передача токена приглашения в onLoginSuccess",
        invitationToken,
      );
      onLoginSuccess(invitationToken);
    },
    onError,
  });

  const signInWithGoogleMutation = useMutation({
    mutationFn: async () => {
      const result = await signInWithPopup(auth, provider);

      return result;
    },
    onSuccess: async (result) => {
      const token = await result.user.getIdToken();

      const isNewUser = getAdditionalUserInfo(result)?.isNewUser;

      // collect phone and complete signup with google
      if (isNewUser) {
        sessionStorage.setItem(USER_TOKEN, token);
        // $ Сохраняем токен приглашения в sessionStorage для использования после завершения регистрации
        if (invitationToken) {
          sessionStorage.setItem("invitationToken", invitationToken);
        }
        navigate(`/${FINISH_GOOGLE_AUTH_ROUTE}`);
      } else {
        await UserService.login({ idToken: token });
        // $ Передаем токен приглашения в функцию onLoginSuccess
        onLoginSuccess(invitationToken);
      }
    },
    onError,
  });

  const navigateTo = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    route: string,
  ) => {
    event.preventDefault();

    const emailValue = emailRef.current?.value || "";
    const state: RouterState = { email: emailValue };

    // $ Добавляем токен к URL если он существует
    if (invitationToken) {
      navigate(`${route}?invitationToken=${invitationToken}`, {
        state,
      });
    } else {
      navigate(route, {
        state,
      });
    }
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const emailValue = event.target.value;
    setEmail(emailValue);

    validateEmail(emailValue);
  };

  const validateEmail = (email: string): boolean => {
    if (!isEmailValid(email)) {
      emailRef.current?.setCustomValidity(t("errors.invalidEmail"));
      return true;
    } else {
      emailRef.current?.setCustomValidity("");
      return false;
    }
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const passwordValue = event.target.value;
    setPassword(passwordValue);

    validatePassword(passwordValue);
  };

  const validatePassword = (password: string): boolean => {
    if (!isPasswordValid(password)) {
      passwordRef.current?.setCustomValidity(t("errors.invalidPassword"));
      return true;
    } else {
      passwordRef.current?.setCustomValidity("");
      return false;
    }
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (
    event,
  ) => {
    event.preventDefault();
    errorRef.current = null;
    setWasValidated(true);

    const form = event.currentTarget;

    validateEmail(email);
    validatePassword(password);
    if (!form.checkValidity()) {
      return;
    }

    signInWithEmailMutation.mutate({ email, password });
  };

  if (isRegionPending) return null;

  const isLoading =
    signInWithEmailMutation.isPending || signInWithGoogleMutation.isPending;
  const disableGoogleAuth = isRussianUser && isProduction;

  return (
    <AuthWrapper>
      <LoadingOverlay isLoading={isLoading} />

      <Box sx={{ width: "100%", maxWidth: 400 }}>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Typography variant="h3" sx={{ mb: 1, fontWeight: 700 }}>
            {t("login.title")}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t("login.subtitle")}
          </Typography>
        </Box>

        {/* Form */}
        <Box component="form" noValidate onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {errorRef.current !== null && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {errorRef.current}
              </Alert>
            )}

            <TextField
              fullWidth
              type="email"
              inputRef={emailRef}
              value={email}
              onChange={handleEmailChange}
              label={t("login.email")}
              error={wasValidated && emailRef.current?.validity.valid === false}
              helperText={wasValidated && emailRef.current?.validationMessage}
              variant="outlined"
            />

            <Box>
              <PasswordField
                id="password"
                label={t("shared.password")}
                value={password}
                onChange={handlePasswordChange}
                inputRef={passwordRef}
                error={
                  wasValidated && passwordRef.current?.validity.valid === false
                }
                helperText={
                  wasValidated && passwordRef.current?.validationMessage
                }
              />
              <Box sx={{ mt: 1, textAlign: "right" }}>
                <Link
                  component={RouterLink}
                  to={`/${RECOVER_PASSWORD_ROUTE}`}
                  onClick={(event) =>
                    navigateTo(event, `/${RECOVER_PASSWORD_ROUTE}`)
                  }
                  variant="body2"
                  color="primary"
                  sx={{
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {t("login.forgotPassword")}
                </Link>
              </Box>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ py: 1.5 }}
            >
              {t("login.signIn")}
            </Button>

            {!disableGoogleAuth && (
              <>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("login.divider")}
                  </Typography>
                </Divider>

                <Tooltip
                  title={
                    disableGoogleAuth ? t("login.googleAuthUnavailable") : ""
                  }
                  placement="top"
                  arrow
                >
                  <span>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="large"
                      startIcon={<GoogleIcon />}
                      onClick={() =>
                        !disableGoogleAuth && signInWithGoogleMutation.mutate()
                      }
                      disabled={disableGoogleAuth}
                      sx={{
                        py: 1.5,
                      }}
                    >
                      {t("login.signInWithGoogle")}
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
          </Stack>

          <Box sx={{ textAlign: "center", mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {`${t("login.notRegistered")} `}
              <Link
                component={RouterLink}
                to={`/${REGISTER_ROUTE}`}
                onClick={(event) => navigateTo(event, `/${REGISTER_ROUTE}`)}
                sx={{
                  textDecoration: "none",
                  fontWeight: 500,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {t("login.signUp")}
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    </AuthWrapper>
  );
}
